import { authAxios } from "utils/axios";
import {
  REF_STORAGE_KEY,
  parseRefId,
} from "components/Profile/ProfileReferals/helpers";

export interface OtpRequestResponse {
  status: string;
}

export interface OtpVerifyResponse {
  status: string;
  api_key?: string;
  message?: string;
}

/**
 * Request a passwordless one-time sign-in code for an email.
 *
 * The backend response is uniform ("sent") whether or not the email has an
 * account (no enumeration); a brand-new email is auto-registered on the first
 * correct code. Rate-limited server-side (429 on limit).
 */
export const requestOtp = async (
  email: string
): Promise<OtpRequestResponse> => {
  const response = await authAxios.post<OtpRequestResponse>(
    "/auth/otp/request",
    { email }
  );
  return response.data;
};

/**
 * Verify a one-time code and start the session (same cookie/token as the old
 * login). Returns the account API key on success.
 */
export const verifyOtp = async (
  email: string,
  code: string
): Promise<OtpVerifyResponse> => {
  const body: { email: string; code: string; ref_id?: number } = {
    email,
    code,
  };

  // Thread a captured `?ref=<inviter users.id>` link through OTP sign-in. The
  // backend applies it ONLY when this verify creates a brand-new account (OTP
  // is the sole signup path now); returning users ignore it. Best-effort —
  // invalid/absent id is simply omitted and never blocks sign-in.
  const refId = parseRefId(localStorage.getItem(REF_STORAGE_KEY));
  if (refId !== null) {
    body.ref_id = refId;
  }

  const response = await authAxios.post<OtpVerifyResponse>(
    "/auth/otp/verify",
    body
  );
  return response.data;
};
